import axios from 'axios';
import moment from 'moment';

import { virtualAirports, virtualOperators } from '../common/objects/consts';
import { historyService } from './history';
import { offerTypes } from '../common/objects/offer_types';
import { urls } from './urls';

const replaceForeignCharacters = text => {
  const dict = {
    261: 'a',
    263: 'c',
    347: 's',
    324: 'n',
    243: 'o',
    322: 'l',
    281: 'e',
    378: 'z',
    252: 'u',
    246: 'o',
    228: 'a'
  };
  return text
    .toLowerCase()
    .replace(
      /[ąćśńółęźüöä]/g,
      c => (dict[c.charCodeAt(0)] ? dict[c.charCodeAt(0)] : c)
    );
};

const axios_json_headers = {
  'Content-Type': 'application/json'
};

const fetchFilterData = action => {
  const url = `${urls[action]}`;
  return axios.get(url);
};

const checkAvailability = params => {
  const url = `${
    urls['offer_availability_check']
  }?${historyService.parseObject2Params(params)}`;
  return axios.get(url);
};

const fetchOfferList = (params, cancelToken) => {
  const url = `${urls['offers_list']}?${historyService.parseObject2Params(
    params
  )}`;
  return axios.get(url, {
    cancelToken: cancelToken,
    headers: axios_json_headers
  });
};

const offerVariantsList = params => {
  const url = `${
    urls['offer_variants_list']
  }?${historyService.parseObject2Params(params)}`;

  return axios.get(url);
};

const unixTime2Date = timestamp =>
  moment
    .unix(timestamp)
    .utc()
    .format('DD.MM.YYYY');

const unixTime2Hour = timestamp =>
  moment
    .unix(timestamp)
    .utc()
    .format('HH:mm');

const parseDate2Unix = date_string =>
  moment(date_string)
    .startOf('day')
    .unix();

const getDepartureAirportIATAFromOffer = offer => {
  const isDepartureFlightPresent =
    offer.flights &&
    offer.flights.length > 0 &&
    offer.flights[0].departure_airport_iata;
  return isDepartureFlightPresent
    ? offer.flights[0].departure_airport_iata
    : '';
};

const destinationAirport = offer => {
  if (!offer || !offer.flights) {
    return null;
  }

  if (
    ((offer.offer_type == offerTypes.one_way_flight &&
      offer.flights.length > 1) ||
      offer.flights.length > 2) &&
    offer.destination_airport_iata
  ) {
    return offer.flights.find(f => {
      return f.arrival_airport_iata == offer.destination_airport_iata;
    });
  }
  return offer.flights[0];
};

const returnAirport = offer => {
  if (!offer || !offer.flights) {
    return null;
  }

  if (
    ((offer.offer_type == offerTypes.one_way_flight &&
      offer.flights.length > 1) ||
      offer.flights.length > 2) &&
    offer.destination_airport_iata
  ) {
    return offer.flights.find(f => {
      return f.departure_airport_iata == offer.destination_airport_iata;
    });
  } else if (
    offer.offer_type == offerTypes.one_way_flight &&
    offer.flights.length == 1
  ) {
    return offer.flights[0];
  }
  return offer.flights.length == 2 ? offer.flights[1] : null;
};

const defaultDateFilters = () => ({
  from: moment()
    .startOf('day')
    .add(1, 'day')
    .unix(),
  to: moment()
    .startOf('day')
    .add(2, 'months')
    .unix()
});

const seoDateFilters = params => ({
  from: moment()
    .startOf('day')
    .add(params.dates_offset, 'days')
    .unix(),
  to: moment()
    .startOf('day')
    .add(params.dates_offset, 'days')
    .add(params.dates_delta, 'days')
    .unix()
});

const groupReservationDefaultDateFilters = () => ({
  from: moment()
    .startOf('day')
    .add(1, 'day')
    .unix(),
  to: moment()
    .startOf('day')
    .add(1, 'months')
    .unix()
});

const localstorageAvailable = () => {
  let test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

const fetchDocuments = params =>
  axios({
    method: 'get',
    url: urls['reservation_documents'],
    params,
    headers: {
      'Content-Type': 'application/json'
    }
  });

const bookOffer = (params, withInsurance, csrfToken) =>
  axios({
    method: 'post',
    url: withInsurance ? urls['book_offer_with_insurance'] : urls['book_offer'],
    data: params,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    }
  });

const groupReserve = (params, csrfToken) =>
  axios({
    method: 'post',
    url: urls['group_reservation'],
    data: params,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    }
  });

const fetchInsurancesList = params =>
  axios({
    method: 'get',
    url: urls['get_traffics_insurances'],
    params,
    headers: {
      'Content-Type': 'application/json'
    }
  });

const sendReservation = (params, token, options) => {
  const url = options.as_agent
    ? urls.send_agent_reservation
    : urls.send_reservation;
  return axios.post(url, params, {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token
    }
  });
};

const getOfferDescription = params =>
  axios({
    method: 'get',
    url: urls['offer_description'],
    params,
    headers: {
      'Content-Type': 'application/json'
    }
  });

const getHotelDetails = params =>
  axios({
    method: 'get',
    url: urls['get_hotel'],
    params,
    headers: {
      Accept: 'application/json'
    }
  });

const link2OfferWithHotel = params =>
  `${urls['offer_show']}?${historyService.parseObject2Params(params)}`;

const link2OfferDescriptionWithHotel = params =>
  `${urls['hotel_show']}?${historyService.parseObject2Params(params)}`;

const filterParameters = (params, allowed_params) => {
  const temp_params = {};
  Object.keys(params).map(param => {
    if (allowed_params.indexOf(param) > -1) {
      if (params[param] != 'undefined') {
        temp_params[param] = params[param];
      }
    }
  });
  return temp_params;
};

const link2ShowHotel = (params, link) => {
  const seo_frendly_params = [
    'departure_date',
    'return_date',
    'adults_number',
    'departure_airports',
    'children_ages',
    'boards',
    'price_max_eur',
    'facilities',
    'duration_min',
    'duration_max',
    'operator_code',
    'accommodations',
    'parser'
  ];
  const standard_params = seo_frendly_params.concat(['id', 'offer_type']);

  let filtered_params = link
    ? filterParameters(params, seo_frendly_params)
    : filterParameters(params, standard_params);
  return `${
    link ? link : urls['hotel_show']
  }?${historyService.parseObject2Params(filtered_params)}`;
};

const link2OfferFlight = params =>
  `${urls['reservation']}?${historyService.parseObject2Params(params)}`;

const link2OfferReservation = params =>
  `${urls['reservation']}?${historyService.parseObject2Params(params)}`;

const formatInterchangeDate = date => {
  let now = moment();
  return moment.utc(date).diff(now) < 0 || !date
    ? ' - '
    : moment.utc(date).format('DD-MM-YYYY');
};

const formatInterchangeTime = date =>
  date ? moment.utc(date).format('HH:mm') : ' - ';

const updateDateRange = (name, date, current) => {
  let today = moment().startOf('day');

  switch (name) {
    case 'startDate':
      if (date >= current.endDate) {
        let new_date = moment
          .unix(date)
          .subtract(
            moment.duration(
              moment.unix(current.startDate).diff(moment.unix(current.endDate))
            )
          )
          .unix();

        return { startDate: date, endDate: new_date };
      }
      return { startDate: date };

    case 'endDate':
      if (date <= current.startDate) {
        let new_date = moment
          .unix(date)
          .add(
            moment.duration(
              moment.unix(current.startDate).diff(moment.unix(current.endDate))
            )
          )
          .unix();

        if (new_date <= today.unix()) {
          return { startDate: today.add(1, 'day').unix(), endDate: date };
        } else {
          return { startDate: new_date, endDate: date };
        }
      }
      return { endDate: date };
  }
};

const createRegionsObject = regions_list => {
  let regions_data = {},
    regions = {},
    main_regions = [];
  regions_list.regions = regions_list.regions
    ? regions_list.regions
    : regions_list;
  regions_list.regions.map(v => {
    v.unicodeName = replaceForeignCharacters(v.name);
    if (v.parent) {
      if (regions[v.parent]) {
        // juz istnieje
        if (regions[v.parent].subregions) {
          regions[v.parent].chosen_count = 0;
          regions[v.parent].subregions.push(v.id);
        } else {
          regions[v.parent].subregions = [v.id];
        }
      } else {
        // jeszcze nie istnieje
        regions[v.parent] = { subregions: [v.id] };
      }

      if (regions[v.id]) {
        v.chosen_count = 0;
        let temp = regions[v.id].subregions;
        regions[v.id] = v;
        regions[v.id].subregions = temp;
      } else {
        regions[v.id] = v;
      }
    } else {
      if (regions[v.id]) {
        let temp = regions[v.id].subregions;
        regions[v.id] = v;
        regions[v.id].subregions = temp;
      } else {
        regions[v.id] = v;
      }
      v.chosen_count = 0;
      main_regions.push(v);
    }
  });

  regions_list.regions.map(region => {
    if (regions[region.id].subregions) {
      regions[region.id].subregions = regions[region.id].subregions.sort(
        (idA, idB) =>
          regions[idA].unicodeName < regions[idB].unicodeName
            ? -1
            : regions[idA].unicodeName > regions[idB].unicodeName
              ? 1
              : 0
      );
    }
  });
  regions_data.regions = regions;
  regions_data.regions_list = regions_list.regions
    .sort(
      (a, b) =>
        a.unicodeName < b.unicodeName
          ? -1
          : a.unicodeName > b.unicodeName
            ? 1
            : 0
    )
    .map(region => region.id);
  regions_data.regions.main_regions = main_regions
    .sort(
      (a, b) =>
        a.unicodeName < b.unicodeName
          ? -1
          : a.unicodeName > b.unicodeName
            ? 1
            : 0
    )
    .map(region => region.id);

  regions_data.special_regions = regions_list.special_regions
    ? regions_list.special_regions
    : [];

  return regions_data;
};

const createAirportsObject = airports_list => {
  let airports_data = {},
    airports = { countries: {} };
  airports_list.map(v => {
    v.unicodeName = `${replaceForeignCharacters(v.name)}?${v.iata}?`;
    airports[v.iata] = v;

    if (!airports.countries[v.country]) {
      airports.countries[v.country] = [v];
    } else {
      airports.countries[v.country].push(v);
    }
  });

  airports_data.airports_list = airports_list.sort(
    (a, b) =>
      a.unicodeName < b.unicodeName ? -1 : a.unicodeName > b.unicodeName ? 1 : 0
  );

  Object.keys(virtualAirports).map(iata => {
    airports[iata] = virtualAirports[iata];
    airports_list.push(virtualAirports[iata]);
  });

  airports_data.airports_list = airports_list;
  airports_data.airports = airports;

  return airports_data;
};

const createFacilitiesObject = facilities_data => {
  let facilities = {},
    categories = {};
  facilities_data.map(facility => {
    facilities[facility.id] = facility;
    if (categories[facility.category]) {
      categories[facility.category].facilities.push(facility);
    } else {
      categories[facility.category] = {
        facilities: [facility]
      };
    }
  });
  categories.list = Object.keys(categories);
  return {
    facilities_list: facilities_data,
    facilities,
    categories
  };
};

const createOperatorsObject = operators_data => {
  let operators = {},
    countries = {};
  operators_data.map(operator => {
    operators[operator.code] = operator;
    if (countries[operator.country]) {
      countries[operator.country].operators.push(operator);
    } else {
      countries[operator.country] = {
        operators: [operator]
      };
    }
  });

  Object.keys(virtualOperators).map(operator => {
    operators_data.push(virtualOperators[operator]);
    operators[operator.code] = virtualOperators[operator];
  });

  countries.list = Object.keys(countries);
  return {
    operators_list: operators_data,
    operators,
    countries
  };
};

const numeralTranslation = (number, variants, option = 0) => {
  if (number === 0) {
    option = 2;
  } else if (number === 1) {
    option = 0;
  } else if (number >= 5 && number <= 21) {
    option = 2;
  } else if (number % 10 >= 2 && number % 10 <= 4) {
    option = 1;
  } else {
    option = 2;
  }
  return variants[option] ? variants[option] : variants[0];
};

const isFilterExpired = (filter, expire) => {
  const expired_time =
    parseInt(localStorage.getItem(`${filter}_filter_last_update`)) + expire;
  return expired_time > new Date().getTime() &&
    localStorage.getItem(`${filter}_filter`)
    ? false
    : true;
};

const load_facebook_api = (d, s, id) => {
  // code adapted from facebook for developers page.
  // https://developers.facebook.com/docs/plugins/like-button
  // it is necessary to load this code on componentDidMount
  let js,
    fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) return;
  js = d.createElement(s);
  js.id = id;
  js.src = '//connect.facebook.net/pl_PL/sdk.js#xfbml=1&version=v2.9';
  fjs.parentNode.insertBefore(js, fjs);
};

// add email to application newsletter's database
const add_email_to_newsletter_db = (new_email, csrf_token) =>
  axios({
    method: 'post',
    url: urls['newsletter_sign_up'],
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    params: {
      'newsletter[email]': new_email
    }
  });

const ask_for_offer = (data, csrf_token) =>
  axios({
    method: 'post',
    url: urls['ask_for_offer'],
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });

const send_on_email = (data, csrf_token) =>
  axios({
    method: 'post',
    url: urls['send_on_email'],
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });

const isDesktopDevice = () => {
  return typeof window != 'undefined' && window.screen.availWidth > 1023;
};

const add_to_favourites = (fav_uuid, data, csrf_token) => {
  let url = fav_uuid
    ? urls['add_to_favourites'].format({ fav_uuid: fav_uuid })
    : urls['add_to_new_favourites'];
  return axios({
    method: 'post',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });
};

const remove_from_favourites = (fav_uuid, data, csrf_token) => {
  let url = urls['remove_from_favourites'].format({ fav_uuid: fav_uuid });
  return axios({
    method: 'post',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });
};

const remove_all_from_favourites = (fav_uuid, csrf_token) => {
  let url = urls['remove_all_from_favourites'].format({ fav_uuid: fav_uuid });
  return axios({
    method: 'post',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    }
  });
};

const send_favourites_on_email = (fav_uuid, data, csrf_token) => {
  let url = urls['send_favourites_on_email'].format({ fav_uuid: fav_uuid });
  return axios({
    method: 'post',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });
};

const send_favourites_pdf_on_email = (fav_uuid, data, csrf_token) => {
  let url = urls['send_favourites_pdf_on_email'].format({ fav_uuid: fav_uuid });
  return axios({
    method: 'post',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf_token
    },
    data: data
  });
};

const send_contact_me_message = (email, phone, offerLink) => {
  let url = urls['send_contact_me_message'];
  return axios({
    method: 'post',
    url: url,
    data: {
      email,
      phone,
      offerLink
    }
  });
};

const fetchCuriosity = params => {
  let url = urls['get_curiosities'];
  return axios({
    method: 'get',
    url: url,
    params,
    headers: {
      'Content-Type': 'application/json'
    }
  });
};

const updateQueryString = (key, value, url) => {
  if (!url) url = window.location.href;
  let re = new RegExp('([?&])' + key + '=.*?(&|#|$)(.*)', 'gi'),
    hash;

  if (re.test(url)) {
    if (typeof value !== 'undefined' && value !== null) {
      return url.replace(re, '$1' + key + '=' + value + '$2$3');
    } else {
      hash = url.split('#');
      url = hash[0].replace(re, '$1$3').replace(/(&|\?)$/, '');
      if (typeof hash[1] !== 'undefined' && hash[1] !== null)
        url += '#' + hash[1];
      return url;
    }
  } else {
    if (typeof value !== 'undefined' && value !== null) {
      let separator = url.indexOf('?') !== -1 ? '&' : '?';
      hash = url.split('#');
      url = hash[0] + separator + key + '=' + value;
      if (typeof hash[1] !== 'undefined' && hash[1] !== null)
        url += '#' + hash[1];
      return url;
    } else {
      return url;
    }
  }
};

const flatten = arr => {
  return arr.reduce(
    (flat, toFlatten) =>
      flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten),
    []
  );
};

const is_object_of_nulls = obj => {
  return obj
    ? Object.keys(obj)
        .map(key => obj[key])
        .filter(val => val !== null).length === 0
    : true;
};

const is_weather_data_empty = weather => {
  if (weather && Object.keys(weather).length > 0) {
    let values = [
      Object.keys(weather.rain_days).map(key => weather.rain_days[key]),
      Object.keys(weather.sunshine_hours).map(
        key => weather.sunshine_hours[key]
      ),
      Object.keys(weather.temperatures).map(key =>
        Object.keys(weather.temperatures[key]).map(
          _key => weather.temperatures[key][_key]
        )
      )
    ];
    return flatten(values).filter(val => val !== null).length > 0;
  }

  return false;
};

const display_weather_widget = weather => {
  if (weather && Object.keys(weather).length > 0) {
    let values = [
      Object.keys(weather.temperatures).map(key =>
        Object.keys(weather.temperatures[key]).map(
          _key => weather.temperatures[key][_key]
        )
      )
    ];
    return flatten(values).filter(val => val !== null).length > 0;
  }

  return false;
};

const parse_weather_object = weather => {
  // sepember - not september - ibe.schemeterling error
  let weather_data = [],
    months = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'sepember',
      'october',
      'november',
      'december'
    ];
  if (
    weather &&
    weather.temperatures &&
    !is_object_of_nulls(weather.temperatures.day) &&
    !is_object_of_nulls(weather.temperatures.night)
  ) {
    weather_data.push({
      values: {
        day: months.map(month => parseInt(weather.temperatures.day[month])),
        night: months.map(month => parseInt(weather.temperatures.night[month])),
        min: Math.min.apply(
          null,
          Object.keys(weather.temperatures.night).map(
            key => weather.temperatures.night[key]
          )
        ),
        max: Math.max.apply(
          null,
          Object.keys(weather.temperatures.day).map(
            key => weather.temperatures.day[key]
          )
        )
      },
      type: 'temperature-day-night'
    });
  }

  if (
    weather &&
    weather.temperatures &&
    !is_object_of_nulls(weather.temperatures.day) &&
    is_object_of_nulls(weather.temperatures.night)
  ) {
    weather_data.push({
      values: {
        day: months.map(month => parseInt(weather.temperatures.day[month])),
        min: 0,
        max: Math.max.apply(
          null,
          Object.keys(weather.temperatures.day).map(
            key => weather.temperatures.day[key]
          )
        )
      },
      type: 'temperature-only-day'
    });
  }

  if (
    weather &&
    weather.temperatures &&
    !is_object_of_nulls(weather.temperatures.water)
  ) {
    weather_data.push({
      values: months.map(month => parseInt(weather.temperatures.water[month])),
      max: Math.max.apply(
        null,
        Object.keys(weather.temperatures.water).map(
          key => weather.temperatures.water[key]
        )
      ),
      type: 'temperature-water'
    });
  }

  if (weather && !is_object_of_nulls(weather.sunshine_hours)) {
    weather_data.push({
      values: months.map(month => parseInt(weather.sunshine_hours[month])),
      max: Math.max.apply(
        null,
        Object.keys(weather.sunshine_hours).map(
          key => weather.sunshine_hours[key]
        )
      ),
      type: 'temperature-sunlight-hours'
    });
  }

  if (weather && !is_object_of_nulls(weather.rain_days)) {
    weather_data.push({
      values: months.map(month => parseInt(weather.rain_days[month])),
      max: Math.max.apply(
        null,
        Object.keys(weather.rain_days).map(key => weather.rain_days[key])
      ),
      type: 'temperature-rainy-days'
    });
  }

  return weather_data;
};

const month_name = date => {
  if (isNaN(date)) {
    //date not in timestamp format
    return moment(date)
      .utc()
      .format('MMMM')
      .toLowerCase();
  } else {
    return moment
      .unix(date)
      .utc()
      .format('MMMM')
      .toLowerCase();
  }
};

const get_upgrades_data = params => {
  const url = `${urls['hotel_upgrades']}?${historyService.parseObject2Params(
    params
  )}`;
  return axios.get(url);
};

const get_hotel_facts = params => {
  const url = `${urls['hotel_facts']}?${historyService.parseObject2Params(
    params
  )}`;
  return axios.get(url);
};

const debounce = (fn, delay) => {
  let timer = null;
  return function() {
    let context = this,
      args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() {
      fn.apply(context, args);
    }, delay);
  };
};

const prepareOfferUniqueKey = offer => {
  const operatorCode =
    offer.operator && offer.operator.code ? offer.operator.code : '';
  const departureFlightIATA = getDepartureAirportIATAFromOffer(offer);
  return `${offer.uuid}-${offer.departure_date}-${
    offer.return_date
  }-${operatorCode}-${departureFlightIATA}`;
};

// create an array literal conditionally [...insertIf(true, 'something')]
const insertIf = (condition, element) => (condition ? [element] : []);

const fetchHotelsList = (offerType, regionId, hotelName) => {
  let params = {
    ...(offerType && { offer_type: offerType }),
    ...(regionId && { regions: regionId }),
    ...(hotelName && { hotel_name: hotelName })
  };

  const url = `${urls['get_hotels_list']}?${historyService.parseObject2Params(
    params
  )}`;
  return axios.get(url);
};

export {
  checkAvailability,
  fetchOfferList,
  offerVariantsList,
  unixTime2Date,
  parseDate2Unix,
  sendReservation,
  getOfferDescription,
  unixTime2Hour,
  defaultDateFilters,
  seoDateFilters,
  link2OfferWithHotel,
  link2ShowHotel,
  link2OfferFlight,
  link2OfferDescriptionWithHotel,
  localstorageAvailable,
  link2OfferReservation,
  formatInterchangeDate,
  formatInterchangeTime,
  updateDateRange,
  createRegionsObject,
  createAirportsObject,
  createFacilitiesObject,
  createOperatorsObject,
  numeralTranslation,
  replaceForeignCharacters,
  fetchFilterData,
  isFilterExpired,
  load_facebook_api,
  add_email_to_newsletter_db,
  isDesktopDevice,
  ask_for_offer,
  send_on_email,
  add_to_favourites,
  remove_from_favourites,
  remove_all_from_favourites,
  send_favourites_on_email,
  send_favourites_pdf_on_email,
  fetchCuriosity,
  getHotelDetails,
  updateQueryString,
  is_weather_data_empty,
  parse_weather_object,
  display_weather_widget,
  month_name,
  get_upgrades_data,
  filterParameters,
  get_hotel_facts,
  debounce,
  destinationAirport,
  returnAirport,
  send_contact_me_message,
  getDepartureAirportIATAFromOffer,
  prepareOfferUniqueKey,
  insertIf,
  fetchHotelsList,
  fetchDocuments,
  fetchInsurancesList,
  bookOffer,
  groupReserve,
  groupReservationDefaultDateFilters
};



// WEBPACK FOOTER //
// ./app/javascript/services/services.js